import React, { useState, useEffect } from 'react';
import { apiFetch, getUserFacingErrorMessage } from '../services/apiClient';
import './QuestionnaireModal.css';

const QuestionnaireModal = ({ isOpen, onClose, resume, jobDescription, onGenerateQuestionnaire }) => {
    const [error, setError] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [questionNotes, setQuestionNotes] = useState({});
    const [questionScores, setQuestionScores] = useState({});
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationComplete, setGenerationComplete] = useState(false);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setError(null);
            setCurrentQuestionIndex(0);
            setQuestionNotes({});
            setQuestionScores({});
            setIsGenerating(false);
            setGenerationComplete(false);
            
            // Show introduction question immediately
            showIntroductionQuestion();
            
            // Generate other questions in background
            generateQuestions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Show introduction question immediately
    const showIntroductionQuestion = () => {
        const introQuestion = {
            id: 'introduction_0',
            section: 'introduction',
            questionNumber: 1,
            question: 'Please walk us through your professional background, highlighting the key experiences and skills that align with this role. What motivates you to pursue this position, and how do you believe your unique combination of expertise makes you the ideal candidate?',
            expected_answer_summary: [
                'Clear articulation of relevant professional background',
                'Specific examples of experiences that align with the role',
                'Demonstration of motivation and passion for the position',
                'Unique value proposition and expertise combination',
                'Understanding of role requirements and how background fits'
            ],
            weightage: 10
        };
        
        setQuestions([introQuestion]);
        setGenerationComplete(true); // Allow navigation immediately
    };

    const POLL_INTERVAL_MS = 2500;
    const POLL_TIMEOUT_MS = 3 * 60 * 1000;

    const pollQuestionnaireStatus = async (resumeId) => {
        const deadline = Date.now() + POLL_TIMEOUT_MS;
        while (Date.now() < deadline) {
            const res = await apiFetch(`/api/resumes/${resumeId}/questionnaire-status`);
            const body = await res.json().catch(() => ({}));
            if (body.ready && body.questionnaire) return body.questionnaire;
            if (body.error && res.status >= 400) throw new Error(body.error || 'Poll failed');
            await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
        }
        throw new Error('Questionnaire generation timed out. Please try again.');
    };

    // Generate other questions in background
    const generateQuestions = async () => {
        if (isGenerating) return;
        
        setIsGenerating(true);
        setError(null);
        
        try {
            const response = await apiFetch('/api/generate-questionnaire', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    resume_id: resume.id,
                    job_description: jobDescription,
                    candidate_name: resume.candidate_name,
                    fit_score: resume.analysis?.fit_score
                })
            });

            const data = await response.json().catch(() => ({}));

            let questionnaire = data.questionnaire;
            if (response.status === 202 && data.status === 'processing') {
                questionnaire = await pollQuestionnaireStatus(resume.id);
            } else if (!response.ok) {
                throw new Error(
                    getUserFacingErrorMessage(
                        response.status,
                        data,
                        `Request failed (${response.status})`
                    )
                );
            } else if (!questionnaire) {
                throw new Error(
                    getUserFacingErrorMessage(
                        response.status,
                        data,
                        data.error || 'No questionnaire in response'
                    )
                );
            }

            const allQuestions = processAllQuestions(questionnaire);
            setQuestions(allQuestions);
            setGenerationComplete(true);

        } catch (err) {
            setError(err.message);
        } finally {
            setIsGenerating(false);
        }
    };

    // Process all questions into a flat array with proper ordering
    const processAllQuestions = (questionnaire) => {
        const allQuestions = [];
        let questionNumber = 1;

        // Add introduction question first
        allQuestions.push({
            id: 'introduction_0',
            section: 'introduction',
            questionNumber: questionNumber++,
            question: 'Please walk us through your professional background, highlighting the key experiences and skills that align with this role. What motivates you to pursue this position, and how do you believe your unique combination of expertise makes you the ideal candidate?',
            expected_answer_summary: [
                'Clear articulation of relevant professional background',
                'Specific examples of experiences that align with the role',
                'Demonstration of motivation and passion for the position',
                'Unique value proposition and expertise combination',
                'Understanding of role requirements and how background fits'
            ],
            weightage: 10
        });

        // Process technical theoretical questions SECOND
        if (questionnaire.technical?.theoretical && Array.isArray(questionnaire.technical.theoretical)) {
            questionnaire.technical.theoretical.forEach((q, index) => {
                if (q && typeof q === 'object' && q.question) {
                    allQuestions.push({
                        id: `technical_theoretical_${index}`,
                        section: 'technical_theoretical',
                        questionNumber: questionNumber++,
                        question: q.question,
                        expected_answer_summary: q.expected_answer_summary || [],
                        weightage: 6.67
                    });
                }
            });
        }

        // Process technical practical questions THIRD
        if (questionnaire.technical?.practical && Array.isArray(questionnaire.technical.practical)) {
            questionnaire.technical.practical.forEach((q, index) => {
                if (q && typeof q === 'object' && q.question) {
                    allQuestions.push({
                        id: `technical_practical_${index}`,
                        section: 'technical_practical',
                        questionNumber: questionNumber++,
                        question: q.question,
                        expected_answer_summary: q.expected_answer_summary || [],
                        weightage: 6.67
                    });
                }
            });
        }

        // Process behavioral questions FOURTH
        if (questionnaire.behavioral && Array.isArray(questionnaire.behavioral)) {
            questionnaire.behavioral.forEach((q, index) => {
                if (q && typeof q === 'object' && q.question) {
                    allQuestions.push({
                        id: `behavioral_${index}`,
                        section: 'behavioral',
                        questionNumber: questionNumber++,
                        question: q.question,
                        expected_answer_summary: q.expected_answer_summary || [],
                        weightage: 7.5
                    });
                }
            });
        }

        // Process situational questions FIFTH
        if (questionnaire.situational && Array.isArray(questionnaire.situational)) {
            questionnaire.situational.forEach((q, index) => {
                if (q && typeof q === 'object' && q.question) {
                    allQuestions.push({
                        id: `situational_${index}`,
                        section: 'situational',
                        questionNumber: questionNumber++,
                        question: q.question,
                        expected_answer_summary: q.expected_answer_summary || [],
                        weightage: 7.5
                    });
                }
            });
        }

        return allQuestions;
    };

    const handleNext = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const handleNotesChange = (questionId, notes) => {
        setQuestionNotes(prev => ({
            ...prev,
            [questionId]: notes
        }));
    };

    const handleScoreChange = (questionId, score) => {
        setQuestionScores(prev => ({
            ...prev,
            [questionId]: score
        }));
    };

    const handleCompleteQuestionnaire = () => {
        alert('🔧 handleCompleteQuestionnaire called!');
        console.log('🔧 handleCompleteQuestionnaire called!');
        console.log('🔧 onGenerateQuestionnaire function:', typeof onGenerateQuestionnaire);
        console.log('🔧 Questions array:', questions);
        console.log('�� Questions length:', questions.length);
        console.log('🔧 Current question index:', currentQuestionIndex);
        
        // Convert questions to questionnaire format
        const questionnaireData = {
            introduction: questions.filter(q => q.section === 'introduction'),
            behavioral: questions.filter(q => q.section === 'behavioral'),
            situational: questions.filter(q => q.section === 'situational'),
            technical: {
                theoretical: questions.filter(q => q.section === 'technical_theoretical'),
                practical: questions.filter(q => q.section === 'technical_practical')
            }
        };
        
        console.log('🔧 Questionnaire data:', questionnaireData);
        
        // Call the callback to save the questionnaire
        if (onGenerateQuestionnaire) {
            console.log('🔧 Calling onGenerateQuestionnaire...');
            onGenerateQuestionnaire(questionnaireData);
        } else {
            console.error('❌ onGenerateQuestionnaire is not defined!');
        }
        
        // Close the modal
        onClose();
    };

    const handleRetry = () => {
        setError(null);
        generateQuestions();
    };

    if (!isOpen) return null;

    const currentQuestion = questions[currentQuestionIndex];
    const isLastQuestion = currentQuestionIndex === questions.length - 1;
    const isFirstQuestion = currentQuestionIndex === 0;
    
    console.log('🔍 Question flow debug:');
    console.log('🔍 currentQuestionIndex:', currentQuestionIndex);
    console.log('�� questions.length:', questions.length);
    console.log('🔍 isLastQuestion:', isLastQuestion);
    console.log('🔍 currentQuestion:', currentQuestion);

    return (
        <div className="questionnaire-modal-overlay">
            <div className="questionnaire-modal">
                <div className="questionnaire-modal-header">
                    <div className="header-content">
                        <h2>📋 Generate Interview Questions</h2>
                        <p className="header-subtitle">
                            Creating personalized questions for {resume.candidate_name}
                        </p>
                    </div>
                    <button className="close-button" onClick={onClose}>
                        ✕
                    </button>
                </div>

                <div className="questionnaire-modal-content">
                    {error && (
                        <div className="error-section">
                            <h3>❌ Error Generating Questions</h3>
                            <p>{error}</p>
                            <div className="error-actions">
                                <button className="retry-button" onClick={handleRetry}>
                                    🔄 Try Again
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Show background generation indicator */}
                    {isGenerating && (
                        <div className="background-generation-indicator">
                            <div className="generation-spinner"></div>
                            <span>Generating additional questions in background...</span>
                        </div>
                    )}

                    {generationComplete && questions.length > 0 && (
                        <div className="questions-section">
                            <div className="question-navigation">
                                <button 
                                    className="nav-button" 
                                    onClick={handlePrevious}
                                    disabled={isFirstQuestion}
                                >
                                    ← Previous
                                </button>
                                <span className="question-counter">
                                    Question {currentQuestionIndex + 1} of {questions.length}
                                </span>
                                <button 
                                    className="nav-button" 
                                    onClick={handleNext}
                                    disabled={isLastQuestion}
                                >
                                    Next →
                                </button>
                            </div>

                            {currentQuestion && (
                                <div className="question-item">
                                    <div className="question-header">
                                        <div className="question-type-section">
                                            <span className="question-type" data-section={currentQuestion.section}>
                                                {currentQuestion.section === 'introduction' ? 'INTRODUCTION' :
                                                 currentQuestion.section === 'behavioral' ? 'BEHAVIORAL' :
                                                 currentQuestion.section === 'situational' ? 'SITUATIONAL' :
                                                 currentQuestion.section === 'technical_theoretical' ? 'TECHNICAL THEORETICAL' :
                                                 currentQuestion.section === 'technical_practical' ? 'TECHNICAL PRACTICAL' :
                                                 currentQuestion.section.replace('_', ' ').toUpperCase()}
                                            </span>
                                        </div>
                                        <span className="question-number">#{currentQuestion.questionNumber}</span>
                                    </div>
                                    
                                    <div className="question-text">{currentQuestion.question}</div>
                                    
                                    {currentQuestion.expected_answer_summary && currentQuestion.expected_answer_summary.length > 0 && (
                                        <div className="expected-answer-summary-section">
                                            <div className="expected-answer-summary-label">
                                                <span>📋</span>
                                                <span>Expected Answer Summary</span>
                                            </div>
                                            <div className="expected-answer-summary-list">
                                                {currentQuestion.expected_answer_summary.map((point, pointIndex) => (
                                                    <div key={pointIndex} className="expected-answer-point">
                                                        <span className="point-bullet">•</span>
                                                        <span className="point-text">{point}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="notes-section">
                                        <span className="notes-icon">📝</span>
                                        <span className="notes-label">Hiring Manager's Notes</span>
                                    </div>
                                    
                                    <textarea
                                        className="notes-input"
                                        placeholder="Add your evaluation notes here..."
                                        value={questionNotes[currentQuestion.id] || ''}
                                        onChange={(e) => handleNotesChange(currentQuestion.id, e.target.value)}
                                        rows="3"
                                    />
                                    
                                    <div className="question-scoring">
                                        <span className="scoring-label">Score:</span>
                                        <div className="scoring-buttons">
                                            <button 
                                                className={`scoring-button ${questionScores[currentQuestion.id] === 0 ? 'selected' : ''}`}
                                                style={{background: questionScores[currentQuestion.id] === 0 ? '#dc2626' : '#ffffff1a'}}
                                                onClick={() => handleScoreChange(currentQuestion.id, 0)}
                                            >
                                                0
                                            </button>
                                            <button 
                                                className={`scoring-button ${questionScores[currentQuestion.id] === 1 ? 'selected' : ''}`}
                                                style={{background: questionScores[currentQuestion.id] === 1 ? '#ea580c' : '#ffffff1a'}}
                                                onClick={() => handleScoreChange(currentQuestion.id, 1)}
                                            >
                                                1
                                            </button>
                                            <button 
                                                className={`scoring-button ${questionScores[currentQuestion.id] === 2 ? 'selected' : ''}`}
                                                style={{background: questionScores[currentQuestion.id] === 2 ? '#16a34a' : '#ffffff1a'}}
                                                onClick={() => handleScoreChange(currentQuestion.id, 2)}
                                            >
                                                2
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Complete button only shows on last question */}
                            {console.log('🔍 Button render check - isLastQuestion:', isLastQuestion)}
                            
                            {/* TEST: Always show a button */}
                            <div className="completion-actions">
                                <button 
                                    className="test-button"
                                    onClick={() => alert('🧪 TEST BUTTON CLICKED!')}
                                    style={{background: 'red', color: 'white', margin: '10px'}}
                                >
                                    🧪 TEST BUTTON
                                </button>
                                
                                <button 
                                    className="test-complete-button"
                                    onClick={() => {
                                        alert('🔧 SIMPLE TEST BUTTON CLICKED!');
                                        console.log('🔧 Simple test button clicked');
                                        console.log('�� questions:', questions);
                                        console.log('🔧 questions length:', questions ? questions.length : 'undefined');
                                        console.log('🔧 currentQuestionIndex:', currentQuestionIndex);
                                        console.log('🔧 onGenerateQuestionnaire:', typeof onGenerateQuestionnaire);
                                        
                                        // Try to call handleCompleteQuestionnaire
                                        try {
                                            handleCompleteQuestionnaire();
                                        } catch (error) {
                                            console.error('❌ Error calling handleCompleteQuestionnaire:', error);
                                            alert('❌ Error: ' + error.message);
                                        }
                                    }}
                                    style={{background: 'blue', color: 'white', margin: '10px'}}
                                >
                                    🔧 TEST COMPLETE QUESTIONNAIRE
                                </button>
                            </div>
                            
                            {isLastQuestion && (
                                <div className="completion-actions">
                                    {console.log('🔍 Rendering Complete Questionnaire button!')}
                                    <button 
                                        className="complete-questionnaire-button"
                                        onClick={handleCompleteQuestionnaire}
                                    >
                                        ✅ Complete Questionnaire
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuestionnaireModal;
